/**
 * @file Citas (Appointments) — restyled to match design page-citas.jsx.
 *
 * Layout:
 *  - pg-head: title + actions
 *  - pg-stats: 4-cell stats strip
 *  - pg-toolbar: search input
 *  - pg-chip-row: status filters
 *  - pg-table-card with citas-grid rows grouped by day-divider
 *
 * Uses design CSS classes from src/client/styles/cd.css.
 */
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useAppointments } from "../hooks/useAppointments";
import { useDashboard } from "../hooks/useDashboard";

const SVG = { fill: "none", stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round" };

const I = {
  Search: () => (<svg width="14" height="14" viewBox="0 0 24 24" {...SVG} strokeWidth="1.7"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>),
  Plus: () => (<svg width="14" height="14" viewBox="0 0 24 24" {...SVG} strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>),
  File: () => (<svg width="14" height="14" viewBox="0 0 24 24" {...SVG} strokeWidth="1.7"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>),
  Filter: () => (<svg width="14" height="14" viewBox="0 0 24 24" {...SVG} strokeWidth="1.7"><path d="M22 3H2l8 9.5V21l4-2v-6.5L22 3z"/></svg>),
  Calendar: () => (<svg width="16" height="16" viewBox="0 0 24 24" {...SVG} strokeWidth="1.6"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 9h18M8 3v4M16 3v4"/></svg>),
  ChevR: () => (<svg width="14" height="14" viewBox="0 0 24 24" {...SVG} strokeWidth="1.8"><path d="M9 6l6 6-6 6"/></svg>),
};

const initials = (name) =>
  (name || "").split(" ").filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "?";

const formatDay = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dDay = new Date(d);
  dDay.setHours(0, 0, 0, 0);
  const diffDays = Math.round((dDay.getTime() - today.getTime()) / 86400000);
  const base = d.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" });
  const cap = base.charAt(0).toUpperCase() + base.slice(1);
  if (diffDays === 0) return `Hoy · ${cap}`;
  if (diffDays === 1) return `Mañana · ${cap}`;
  if (diffDays === -1) return `Ayer · ${cap}`;
  return cap;
};

const dayKey = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const formatTime = (iso) =>
  new Date(iso).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", hour12: false });

const STATUS_TO_FILTER = {
  AGENDADA: "agendadas",
  CONFIRMADA: "agendadas",
  ASISTENCIA_CONFIRMADA: "agendadas",
  COMPLETADA: "completadas",
  CANCELADA: "canceladas",
  NO_ASISTIO: "canceladas",
};

const PILL_LABEL = {
  AGENDADA: "Agendada",
  CONFIRMADA: "Confirmada",
  ASISTENCIA_CONFIRMADA: "Confirmada",
  COMPLETADA: "Completada",
  CANCELADA: "Cancelada",
  NO_ASISTIO: "No asistió",
};

function StatusPill({ status }) {
  const mapped =
    status === "COMPLETADA" ? "completada" :
    status === "CANCELADA" || status === "NO_ASISTIO" ? "cancelada" :
    "agendada";
  return <span className={"status-pill " + mapped}>{PILL_LABEL[status] || status}</span>;
}

export default function Appointments() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPro = user?.role === "PROFESIONAL";
  const { stats } = useDashboard(isPro);
  const { appointments, isLoading } = useAppointments({ autoFetch: true });

  const [filter, setFilter] = useState("agendadas");
  const [search, setSearch] = useState("");

  const counts = useMemo(() => ({
    agendadas: appointments.filter((a) => ["AGENDADA", "CONFIRMADA", "ASISTENCIA_CONFIRMADA"].includes(a.status)).length,
    completadas: appointments.filter((a) => a.status === "COMPLETADA").length,
    canceladas: appointments.filter((a) => ["CANCELADA", "NO_ASISTIO"].includes(a.status)).length,
    todas: appointments.length,
  }), [appointments]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return appointments
      .filter((a) => {
        if (filter !== "todas") {
          const tag = STATUS_TO_FILTER[a.status];
          if (tag !== filter) return false;
        }
        if (!q) return true;
        const name = `${a.patient?.user?.firstName || ""} ${a.patient?.user?.lastName || ""}`.toLowerCase();
        return name.includes(q);
      })
      .sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime());
  }, [appointments, filter, search]);

  const groups = useMemo(() => {
    const out = {};
    filtered.forEach((a) => {
      const k = dayKey(a.dateTime);
      (out[k] ||= []).push(a);
    });
    return out;
  }, [filtered]);

  const dayKeys = Object.keys(groups);

  return (
    <>
      <div className="pg-head">
          <div>
            <div className="cd-eyebrow">Gestión de agenda</div>
            <h1 className="pg-title">Mis <em>citas</em></h1>
            <p className="pg-sub">Filtra, busca y gestiona todas las citas próximas e históricas.</p>
          </div>
          <div className="pg-actions">
            <button type="button" className="pg-btn" onClick={() => navigate("/calendario")}>
              <I.Calendar /> Calendario
            </button>
            <button type="button" className="pg-btn solid" onClick={() => navigate("/nueva-cita")}>
              <I.Plus /> Nueva cita
            </button>
          </div>
        </div>

        <div className="pg-stats">
          <div className="pg-stat">
            <div className="l">Total agendadas</div>
            <div className="v">{counts.agendadas}</div>
            <div className="d">Próximas en agenda</div>
          </div>
          <div className="pg-stat">
            <div className="l">Hoy</div>
            <div className="v">{stats?.todayCount ?? 0}</div>
            <div className="d">Citas programadas hoy</div>
          </div>
          <div className="pg-stat">
            <div className="l">Completadas (mes)</div>
            <div className="v">{stats?.monthlyCompleted ?? 0}</div>
            <div className="d">Total atendidas este mes</div>
          </div>
          <div className="pg-stat">
            <div className="l">Tasa de confirmación</div>
            <div className="v">{stats?.confirmationRate ?? 0}<span className="unit">%</span></div>
            <div className="d">Confirmadas / total</div>
          </div>
        </div>

        <div className="pg-toolbar">
          <div className="pg-search">
            <I.Search />
            <input
              placeholder="Buscar por paciente…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="pg-toolbar-spacer" />
        </div>

        <div className="pg-chip-row" style={{ marginBottom: 18 }}>
          <button
            type="button"
            className={"pg-chip" + (filter === "agendadas" ? " active" : "")}
            onClick={() => setFilter("agendadas")}
          >
            Agendadas <span className="count">{counts.agendadas}</span>
          </button>
          <button
            type="button"
            className={"pg-chip" + (filter === "completadas" ? " active" : "")}
            onClick={() => setFilter("completadas")}
          >
            Completadas <span className="count">{counts.completadas}</span>
          </button>
          <button
            type="button"
            className={"pg-chip" + (filter === "canceladas" ? " active" : "")}
            onClick={() => setFilter("canceladas")}
          >
            Canceladas <span className="count">{counts.canceladas}</span>
          </button>
          <button
            type="button"
            className={"pg-chip" + (filter === "todas" ? " active" : "")}
            onClick={() => setFilter("todas")}
          >
            Todas <span className="count">{counts.todas}</span>
          </button>
        </div>

        {isLoading ? (
          <div className="pg-empty" style={{ padding: 40 }}>Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="pg-empty">
            <div className="glyph"><I.Calendar /></div>
            <h4>Sin citas en este filtro</h4>
            <p>Cambia el filtro o limpia la búsqueda para ver más resultados.</p>
          </div>
        ) : (
          <div className="pg-table-card">
            <div className="pg-table-head citas-grid">
              <div>Hora</div>
              <div className="col-patient">Paciente</div>
              <div className="col-motivo">Motivo</div>
              <div className="col-status">Estado</div>
              <div className="col-arrow"></div>
            </div>
            {dayKeys.map((dk) => {
              const day = groups[dk][0].dateTime;
              return (
                <React.Fragment key={dk}>
                  <div className="pg-day-divider">
                    <span className="when">{formatDay(day)}</span>
                    <span className="count">
                      {groups[dk].length} {groups[dk].length === 1 ? "cita" : "citas"}
                    </span>
                  </div>
                  {groups[dk].map((c) => {
                    const fullName = `${c.patient?.user?.firstName || ""} ${c.patient?.user?.lastName || ""}`.trim();
                    const start = formatTime(c.dateTime);
                    const end = formatTime(c.endTime);
                    const duration = Math.round(
                      (new Date(c.endTime).getTime() - new Date(c.dateTime).getTime()) / 60000
                    );
                    return (
                      <div
                        key={c.id}
                        className="pg-table-row citas-grid"
                        onClick={() => navigate(`/cita/${c.id}`)}
                      >
                        <div className="cita-time">
                          {start}
                          <span className="end">→ {end}</span>
                        </div>
                        <div className="cita-patient">
                          <div className={"cd-avatar a" + (((fullName.length || 1) % 6) + 1)}>
                            {initials(fullName)}
                          </div>
                          <div>
                            <div className="n">{fullName || "Paciente"}</div>
                            <div className="a">{c.patient?.user?.email || ""}</div>
                          </div>
                        </div>
                        <div className="cita-motivo col-motivo">
                          {c.service?.name || c.motivo || "Consulta"}
                          {duration > 0 && <span className="dur">{duration} min</span>}
                        </div>
                        <div className="cita-status"><StatusPill status={c.status} /></div>
                        <div className="row-arrow col-arrow"><I.ChevR /></div>
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        )}
    </>
  );
}
